import type React from 'react'
import { useState, useEffect } from 'react'
import { Camera } from 'lucide-react'
import { toast } from 'sonner'
import { useAppSelector, useAppDispatch } from '../../app/hooks'
import { fetchCompanyMember, fetchCompany, updateCompany } from '../../features/auth/authAPI'
import { updateCompanyProfilePicture, ensureCurrentUser } from '../../features/auth/authSlice'
import { fetchIndustries, fetchRegions, fetchCountries, fetchCities, clearCountries, clearCities } from '../../features/filters/filterSlice'
import { uploadFile, selectFile } from '../../utils/fileUploadUtils'
import { ProfileImage } from '../ProfileImage'
import FormDropdown from '../form-dropdown'
import PhoneCountrySelect from '../phone-country-select'
import PhoneInput, {
    isValidPhoneNumber,
    isPossiblePhoneNumber,
    parsePhoneNumber,
    type Country,
} from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

interface CompanyFormData {
    companyName: string
    companyWebsite: string
    companyEmail: string
    companyPhone: string
    industryType: number | null
    city: number | null
    country: number | null
    addressLine1: string
    addressLine2: string
    aboutCompany: string
}

const CompanyInfo: React.FC = () => {
    const user = useAppSelector((state) => state.auth.user)
    const accessToken = useAppSelector((state) => state.auth.accessToken)
    const dispatch = useAppDispatch()

    const industries = useAppSelector((state) => state.filters.industries)
    const industriesStatus = useAppSelector((state) => state.filters.industriesStatus)
    const regions = useAppSelector((state) => state.filters.regions)
    const regionsStatus = useAppSelector((state) => state.filters.regionsStatus)
    const countries = useAppSelector((state) => state.filters.countries)
    const countriesStatus = useAppSelector((state) => state.filters.countriesStatus)
    const cities = useAppSelector((state) => state.filters.cities)
    const citiesStatus = useAppSelector((state) => state.filters.citiesStatus)

    const [formData, setFormData] = useState<CompanyFormData>({
        companyName: '',
        companyWebsite: '',
        companyEmail: '',
        companyPhone: '',
        industryType: null,
        city: null,
        country: null,
        addressLine1: '',
        addressLine2: '',
        aboutCompany: '',
    })
    const [companyId, setCompanyId] = useState<number | null>(null)
    const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null)
    const [profilePictureId, setProfilePictureId] = useState<number | null>(null)
    const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [companyPhoneCountry, setCompanyPhoneCountry] = useState<Country | undefined>(undefined)

    // Fetch filters on mount
    useEffect(() => {
        dispatch(fetchIndustries())
        dispatch(fetchRegions())
    }, [dispatch])

    // Fetch countries when region is selected
    useEffect(() => {
        if (selectedRegionId) {
            dispatch(fetchCountries({ regionId: selectedRegionId }))
        } else {
            dispatch(clearCountries())
            setFormData((prev) => ({ ...prev, country: null, city: null }))
        }
    }, [selectedRegionId, dispatch])

    // Fetch cities when country is selected
    useEffect(() => {
        if (formData.country) {
            dispatch(fetchCities({ countryId: formData.country }))
        } else {
            dispatch(clearCities())
            setFormData((prev) => ({ ...prev, city: null }))
        }
    }, [formData.country, dispatch])

    // Fetch company data
    useEffect(() => {
        if (!accessToken) return

        let isMounted = true
        const loadCompanyData = async () => {
            try {
                // Get company_member_id from current user
                let companyMemberId = user?.company_member_id

                if (!companyMemberId) {
                    const ensuredUser = await dispatch(ensureCurrentUser()).unwrap()
                    companyMemberId = ensuredUser.company_member_id as number | undefined
                }

                if (!companyMemberId) {
                    console.error('Company member ID not found')
                    return
                }

                // Fetch company member to get company_id
                const companyMemberResponse = await fetchCompanyMember(companyMemberId, accessToken)
                const companyIdFromMember = companyMemberResponse.data?.company?.id

                if (!companyIdFromMember) {
                    console.error('Company ID not found')
                    return
                }

                if (!isMounted) return
                setCompanyId(companyIdFromMember)

                // Fetch company data
                const companyResponse = await fetchCompany(companyIdFromMember, accessToken)
                if (!isMounted) return

                const data = companyResponse.data
                if (!data) return

                // Extract IDs from nested objects
                const industryId = data.industry?.id ?? null
                const countryId = data.country?.id ?? null
                const cityId = data.city?.id ?? null

                const rawContact = (data.contact_number || '').trim()
                let companyPhone = rawContact
                let resolvedPhoneCountry: Country | undefined
                if (rawContact) {
                    try {
                        const parsed = parsePhoneNumber(rawContact)
                        if (parsed?.isValid()) {
                            companyPhone = parsed.number
                        }
                        resolvedPhoneCountry = parsed?.country
                    } catch {
                        resolvedPhoneCountry = undefined
                    }
                }
                setCompanyPhoneCountry(resolvedPhoneCountry)

                setFormData({
                    companyName: data.name || '',
                    companyWebsite: data.website || '',
                    companyEmail: data.email || '',
                    companyPhone,
                    industryType: industryId,
                    city: cityId,
                    country: countryId,
                    addressLine1: data.address_line_1 || '',
                    addressLine2: data.address_line_2 || '',
                    aboutCompany: data.about || '',
                })

                // Set region first so countries load; then cities load via formData.country
                if (data.region?.id) {
                    setSelectedRegionId(data.region.id)
                    dispatch(fetchCountries({ regionId: data.region.id }))
                }
                if (countryId) {
                    dispatch(fetchCities({ countryId }))
                }

                // Set existing profile picture if available
                if (data.profile_picture) {
                    if (typeof data.profile_picture === 'number') {
                        setProfilePictureId(data.profile_picture)
                    }
                }
            } catch (error) {
                console.error('Failed to fetch company data:', error)
                toast.error('Failed to load company data')
            }
        }

        loadCompanyData()

        return () => {
            isMounted = false
        }
    }, [user?.company_member_id, accessToken, dispatch])

    // Cleanup preview URL on unmount
    useEffect(() => {
        return () => {
            if (profilePicturePreview) {
                URL.revokeObjectURL(profilePicturePreview)
            }
        }
    }, [profilePicturePreview])

    const handleInputChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => {
        const { name, value } = e.target
        if (name === 'region') {
            setSelectedRegionId(value === '' ? null : Number(value))
            return
        }
        setFormData((prev) => ({
            ...prev,
            [name]: name === 'industryType' || name === 'city' || name === 'country'
                ? (value === '' ? null : Number(value))
                : value,
        }))
    }

    const handleImageUpload = async () => {
        if (!accessToken) {
            toast.error('Authentication required to upload image')
            return
        }

        try {
            const file = await selectFile('image/*')
            if (!file) {
                return
            }

            if (!file.type.startsWith('image/')) {
                toast.error('Please select a valid image file')
                return
            }

            const maxSize = 10 * 1024 * 1024 // 10MB
            if (file.size > maxSize) {
                toast.error('Image size must be less than 10MB')
                return
            }

            setIsUploading(true)

            const previewUrl = URL.createObjectURL(file)
            setProfilePicturePreview(previewUrl)

            const fileId = await uploadFile(file, accessToken)

            if (fileId) {
                setProfilePictureId(fileId)
                toast.success('Image uploaded successfully')
            } else {
                setProfilePicturePreview(null)
                toast.error('Failed to upload image. Please try again.')
            }
        } catch (error) {
            console.error('Error uploading image:', error)
            setProfilePicturePreview(null)
            toast.error('Failed to upload image. Please try again.')
        } finally {
            setIsUploading(false)
        }
    }

    const handleSave = async () => {
        if (!accessToken || !companyId) {
            toast.error('Company ID not found')
            return
        }

        if (!formData.companyName.trim()) {
            toast.error('Company name is required')
            return
        }

        try {
            setIsSaving(true)

            const payload = {
                name: formData.companyName,
                website: formData.companyWebsite || null,
                email: formData.companyEmail || null,
                contact_number: formData.companyPhone || null,
                industry: formData.industryType || null,
                country: formData.country || null,
                city: formData.city || null,
                address_line_1: formData.addressLine1 || null,
                address_line_2: formData.addressLine2 || null,
                about: formData.aboutCompany || null,
                profile_picture: profilePictureId || null,
                // departments: [],
            }

            await updateCompany(companyId, accessToken, payload)
            
            // Update company profile picture in Redux state so Header updates immediately
            if (profilePictureId !== null) {
                dispatch(updateCompanyProfilePicture(profilePictureId))
            }
            
            toast.success('Company information updated successfully')
        } catch (error: unknown) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to save changes. Please try again.'
            toast.error(message)
        } finally {
            setIsSaving(false)
        }
    }

    const companyPhoneTrimmed = formData.companyPhone.trim()
    const phoneValidationCountry =
        companyPhoneCountry ||
        (companyPhoneTrimmed
            ? (() => {
                try {
                    return parsePhoneNumber(companyPhoneTrimmed)?.country
                } catch {
                    return undefined
                }
            })()
            : undefined)
    const showCompanyPhoneInvalid =
        /\d/.test(companyPhoneTrimmed) &&
        Boolean(phoneValidationCountry) &&
        isPossiblePhoneNumber(companyPhoneTrimmed, phoneValidationCountry!) &&
        !isValidPhoneNumber(companyPhoneTrimmed)

    return (
        <div className="space-y-6">
            {/* Profile Photo Section */}
            <div className="flex items-center gap-4 mb-8">
                <div className="relative">
                    <div className="w-24 h-24 bg-gray-400 border-2 border-black rounded-full flex items-center justify-center overflow-hidden">
                        {profilePicturePreview ? (
                            <img
                                src={profilePicturePreview}
                                alt="Company preview"
                                className="w-24 h-24 rounded-full object-cover"
                            />
                        ) : profilePictureId ? (
                            <ProfileImage
                                profilePicture={profilePictureId}
                                accessToken={accessToken || undefined}
                                alt="Company"
                                className="w-24 h-24 rounded-full object-cover"
                                fallbackPath="/assets/images/user-Avatar.png"
                            />
                        ) : (
                            <img
                                src="/assets/images/user-Avatar.png"
                                alt="Company placeholder"
                                className="w-24 h-24 rounded-full object-cover"
                            />
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleImageUpload}
                        disabled={isUploading || !accessToken}
                        className="absolute bottom-0 right-0 bg-[#ABD405] p-2 rounded-full border-2 border-black cursor-pointer hover:bg-lime-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Upload Photo"
                    >
                        {isUploading ? (
                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-md animate-spin" />
                        ) : (
                            <Camera size={16} className="text-[#000000]" />
                        )}
                    </button>
                </div>
                <div>
                    <p className="font-medium text-[#000000]">Upload company photo</p>
                    {isUploading && (
                        <p className="text-sm text-gray-500">Uploading...</p>
                    )}
                </div>
            </div>

            {/* Company Name and Website */}
            <div className="grid grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-[#000000] mb-2">
                        Company name
                    </label>
                    <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.companyName ? 'text-gray-800' : 'text-gray-400'}`}
                        placeholder="Enter company name"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-[#000000] mb-2">
                        Company website
                    </label>
                    <input
                        type="text"
                        name="companyWebsite"
                        value={formData.companyWebsite}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.companyWebsite ? 'text-gray-800' : 'text-gray-400'}`}
                        placeholder="Enter company website"
                    />
                </div>
            </div>

            {/* Company Email and Phone */}
            <div className="grid grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-[#000000] mb-2">
                        Company email
                    </label>
                    <input
                        type="email"
                        name="companyEmail"
                        value={formData.companyEmail}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.companyEmail ? 'text-gray-800' : 'text-gray-400'}`}
                        placeholder="Enter company email"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-[#000000] mb-2">
                        Company contact number
                    </label>
                    <PhoneInput
                        international
                        country={companyPhoneCountry}
                        onCountryChange={setCompanyPhoneCountry}
                        countrySelectComponent={PhoneCountrySelect}
                        value={formData.companyPhone}
                        onChange={(val) =>
                            setFormData((prev) => ({
                                ...prev,
                                companyPhone: val ?? '',
                            }))
                        }
                        className={`phone-input-wrapper ${formData.companyPhone ? 'text-gray-800' : 'text-gray-400'}`}
                    />
                    {showCompanyPhoneInvalid && (
                        <div className="text-red-500 text-sm">
                            Please enter a valid phone number
                        </div>
                    )}
                </div>
            </div>

            {/* Region and Industry Type */}
            <div className="grid grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-[#000000] mb-2">
                        Region
                    </label>
                    <FormDropdown
                        value={selectedRegionId ? String(selectedRegionId) : ''}
                        onSelect={(value) => setSelectedRegionId(value === '' ? null : Number(value))}
                        placeholder="Select region"
                        disabled={regionsStatus === 'loading'}
                        options={regions.map((region) => ({
                            label: region.name,
                            value: String(region.id),
                        }))}
                    />
                    {regionsStatus === 'loading' && (
                        <span className="text-gray-400 text-sm ml-2">Loading...</span>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-[#000000] mb-2">
                        Industry type
                    </label>
                    <FormDropdown
                        value={formData.industryType ? String(formData.industryType) : ''}
                        onSelect={(value) =>
                            setFormData((prev) => ({
                                ...prev,
                                industryType: value === '' ? null : Number(value),
                            }))
                        }
                        placeholder="Select industry"
                        disabled={industriesStatus === 'loading'}
                        options={industries.map((industry) => ({
                            label: industry.name,
                            value: String(industry.id),
                        }))}
                    />
                    {industriesStatus === 'loading' && (
                        <span className="text-gray-400 text-sm ml-2">Loading...</span>
                    )}
                </div>
            </div>

            {/* Country and City */}
            <div className="grid grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-[#000000] mb-2">
                        Country
                    </label>
                    <FormDropdown
                        value={formData.country ? String(formData.country) : ''}
                        onSelect={(value) =>
                            setFormData((prev) => ({
                                ...prev,
                                country: value === '' ? null : Number(value),
                            }))
                        }
                        placeholder={!selectedRegionId ? 'Select region first' : 'Select country'}
                        disabled={!selectedRegionId || countriesStatus === 'loading'}
                        options={countries.map((country) => ({
                            label: country.name,
                            value: String(country.id),
                        }))}
                    />
                    {countriesStatus === 'loading' && (
                        <span className="text-gray-400 text-sm ml-2">Loading...</span>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-[#000000] mb-2">
                        City
                    </label>
                    <FormDropdown
                        value={formData.city ? String(formData.city) : ''}
                        onSelect={(value) =>
                            setFormData((prev) => ({
                                ...prev,
                                city: value === '' ? null : Number(value),
                            }))
                        }
                        placeholder={!formData.country ? 'Select country first' : 'Select city'}
                        disabled={!formData.country || citiesStatus === 'loading'}
                        options={cities.map((city) => ({
                            label: city.name,
                            value: String(city.id),
                        }))}
                    />
                    {citiesStatus === 'loading' && (
                        <span className="text-gray-400 text-sm ml-2">Loading...</span>
                    )}
                </div>
            </div>

            {/* Address Line 1 */}
            <div>
                <label className="block text-sm font-medium text-[#000000] mb-2">
                    Address line 1
                </label>
                <input
                    type="text"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.addressLine1 ? 'text-gray-800' : 'text-gray-400'}`}
                    placeholder="Enter address line 1"
                />
            </div>

            {/* Address Line 2 */}
            <div>
                <label className="block text-sm font-medium text-[#000000] mb-2">
                    Address line 2
                </label>
                <input
                    type="text"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.addressLine2 ? 'text-gray-800' : 'text-gray-400'}`}
                    placeholder="Enter address line 2"
                />
            </div>

            {/* About Company */}
            <div>
                <label className="block text-sm font-medium text-[#000000] mb-2">
                    About company
                </label>
                <textarea
                    name="aboutCompany"
                    value={formData.aboutCompany}
                    onChange={handleInputChange}
                    rows={4}
                    className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 resize-none ${formData.aboutCompany ? 'text-gray-800' : 'text-gray-400'}`}
                    placeholder="Enter company description..."
                />
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-cta px-8 py-2 font-semibold rounded-md disabled:opacity-60"
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </div>
    )
}

export default CompanyInfo
